/**
 * Script & Audio Panel - Two-Card Architecture
 *
 * Modern redesign with clear separation of concerns:
 * - Card 1 (AudioReviewCard): Browse & apply generated audio (OUTPUT)
 * - Card 2 (ScriptVoiceCard): Configure script & voice settings (INPUT)
 * - GeneratingOverlay: Progress state with thinking bubble
 *
 * This component serves as the orchestrator, managing all state and handlers,
 * while the sub-components handle the UI rendering.
 */

import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';

// New sub-components (Two-Card Architecture)
import {
  AudioReviewCard,
  ScriptVoiceCard,
  GeneratingOverlay,
} from './narration';
import {
  getSlideAudio,
  createAudioVersion,
  generateAudio,
  updateScript,
  listAudioVersions,
  switchAudioVersion,
  getAvailableVoices,
  getSlidePrompts,
  updateSlidePrompts,
  applyAudioWithChoreography,
  getAudioSettings,
  type SlideAudioResponse,
  type SlideAudioVersion,
  type TTSVoice,
  type WordTiming,
  type AppliedVersionInfo,
} from '@/services/audioService';
import { cancelAgentJob } from '@/services/agentJobService';
import { useApplyJobStatus } from '@/hooks/useApplyJobStatus';
import { useNarrationAudioSrc } from '@/hooks/useNarrationAudioSrc';
import { GateTimingEditor } from './GateTimingEditor';
// AIThinkingCard is now used inside AudioReviewCard

const SHOW_NARRATION_ADVANCED = import.meta.env.VITE_SHOW_NARRATION_ADVANCED === 'true';

interface ScriptAudioPanelProps {
  slideId: string;
  slideIdentifier?: string;  // e.g., "slide-001" - used for file system paths
  conversationId: string;
  parentConversationId?: string;  // Parent video conversation ID for settings sync
  initialScript?: string;
  onScriptChange?: (script: string) => void;
  onAudioGenerated?: (audioUrl: string, wordTimings: WordTiming[]) => void;
  className?: string;
}

export const ScriptAudioPanel: React.FC<ScriptAudioPanelProps> = ({
  slideId,
  slideIdentifier,
  conversationId,
  parentConversationId,
  initialScript = '',
  onScriptChange,
  onAudioGenerated,
  className,
}) => {
  // State
  const [script, setScript] = useState(initialScript);
  const [audioVersion, setAudioVersion] = useState<SlideAudioVersion | null>(null);
  const [versions, setVersions] = useState<SlideAudioVersion[]>([]);
  const [voices, setVoices] = useState<TTSVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('Kore');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [_isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // TTS Prompt state
  const [selectedModel, setSelectedModel] = useState<'flash' | 'pro'>('flash');
  const [promptFlash, setPromptFlash] = useState<string>('');
  const [promptPro, setPromptPro] = useState<string>('');
  const [hasUnsavedPrompts, setHasUnsavedPrompts] = useState(false);
  const [isVideoLevelPrompts, setIsVideoLevelPrompts] = useState(false);

  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Generation progress tracking
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
  const [generationProgress, setGenerationProgress] = useState<number>(0);
  const [_generationMessage, setGenerationMessage] = useState<string>('');

  // Animation choreography state - now managed by useApplyJobStatus hook for persistence
  const {
    applyingSlideIds,
    slideApplyProgress,
    registerJob: registerApplyJob,
  } = useApplyJobStatus(conversationId);

  // Derive sync state from hook (replaces local state)
  const isSyncingAnimations = applyingSlideIds.has(slideId);
  const syncProgress = slideApplyProgress[slideId] || null;
  const [syncError, setSyncError] = useState<string | null>(null);

  // Track choreography job ID for cancellation (hook manages SSE subscription)
  const [choreographyJobId, setChoreographyJobId] = useState<string | null>(null);

  // Apply audio state
  const [isApplyingAudio, setIsApplyingAudio] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Applied version tracking - which audio version is currently applied to the slide
  const [appliedVersion, setAppliedVersion] = useState<AppliedVersionInfo | null>(null);

  // Ref to track generating state for use in callbacks (avoids closure issues)
  const isGeneratingRef = useRef(false);

  // Track the audio version that was active when user clicked generate
  // This ensures we don't consider generation "complete" until we have a NEW version
  const generationStartVersionRef = useRef<number | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  // Note: Version mismatch and applied audio states are now computed inside AudioReviewCard
  // Accordion state is now managed inside ScriptVoiceCard

  // Track previous conversation ID to detect conversation switches
  const prevConversationIdRef = useRef<string | null>(null);

  // CRITICAL: Reset ALL local state when conversationId changes
  // This prevents cross-conversation data contamination
  useEffect(() => {
    // Skip on initial mount (prevConversationIdRef.current is null)
    if (prevConversationIdRef.current && prevConversationIdRef.current !== conversationId) {
      console.log('🔄 [ScriptAudioPanel] Conversation changed, resetting all state:', {
        from: prevConversationIdRef.current,
        to: conversationId,
        slideId,
      });

      // Clear all audio-related state
      setAudioVersion(null);
      setVersions([]);
      setScript(initialScript || '');
      setError(null);
      setHasUnsavedChanges(false);

      // Clear playback state
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setCurrentWordIndex(-1);

      // Clear generation state
      setGenerationProgress(0);
      setGenerationMessage('');
      setIsGenerating(false);
      isGeneratingRef.current = false;
      generationStartVersionRef.current = null;
      setGenerationStartTime(null);

      // Clear sync/apply state (sync state is now managed by hook)
      setSyncError(null);
      setChoreographyJobId(null);
      setIsApplyingAudio(false);
      setApplyError(null);

      // Clear prompts state
      setPromptFlash('');
      setPromptPro('');
      setHasUnsavedPrompts(false);

      setAudioLoadError(null);

      // Reset voice load tracking so voice gets re-loaded for new conversation
      initialVoiceLoadedRef.current = false;

      // Note: Accordion state is now managed by sub-components
    }
    prevConversationIdRef.current = conversationId;
  }, [conversationId, initialScript, slideId]);

  // Load initial data - when slideId OR conversationId changes
  // IMPORTANT: Include conversationId so we reload when switching conversations
  useEffect(() => {
    if (!slideId) {
      console.log('🎧 [ScriptAudioPanel] Skipping load - no slideId');
      return;
    }
    console.log('🎧 [ScriptAudioPanel] Loading data for slideId:', slideId, 'conversationId:', conversationId);
    initialVoiceLoadedRef.current = false;
    loadVoices(slideId, loadAudioData());
    loadPrompts();
  }, [slideId, conversationId]);

  // Note: Auto-collapse behavior is now handled inside ScriptVoiceCard

  // Listen for script-updated events from worker (via SSE)
  useEffect(() => {
    const handleScriptUpdated = (event: CustomEvent<{
      conversationId: string;
      slideId: string;
      scriptText: string;
      timestamp: string;
    }>) => {
      const { slideId: updatedSlideId, scriptText: newScript } = event.detail;

      if (updatedSlideId === slideId) {
        console.log('[ScriptAudioPanel] Script updated via SSE');
        setScript(newScript);
        setHasUnsavedChanges(false);
        loadAudioData();
      }
    };

    window.addEventListener('script-updated', handleScriptUpdated as EventListener);
    return () => {
      window.removeEventListener('script-updated', handleScriptUpdated as EventListener);
    };
  }, [slideId]);

  // Listen for batch script updates (e.g., from video_script_planner)
  // This ensures ScriptAudioPanel reloads from DB when VideoScriptsPanel refreshes
  useEffect(() => {
    const handleBatchUpdate = (event: CustomEvent<{
      conversationId: string;
      timestamp: number;
      source: string;
    }>) => {
      // Reload from DB when parent video scripts are batch-updated
      const targetConversationId = parentConversationId || conversationId;
      if (event.detail.conversationId === targetConversationId) {
        console.log('[ScriptAudioPanel] Batch update received, reloading from DB');
        loadAudioData();
      }
    };

    window.addEventListener('scripts-batch-updated', handleBatchUpdate as EventListener);
    return () => window.removeEventListener('scripts-batch-updated', handleBatchUpdate as EventListener);
  }, [parentConversationId, conversationId]);

  // Update script when initialScript changes
  useEffect(() => {
    if (initialScript && initialScript !== script) {
      setScript(initialScript);
      setHasUnsavedChanges(false);
    }
  }, [initialScript]);

  // Auto-save script changes (debounced 1s)
  useEffect(() => {
    if (!hasUnsavedChanges || !script.trim() || !audioVersion) return;

    const timeoutId = setTimeout(async () => {
      try {
        setIsSaving(true);
        const selectedVoiceObj = voices.find((v) => v.id === selectedVoice);
        await updateScript(slideId, script, {
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
        });
        setHasUnsavedChanges(false);
        console.log('[ScriptAudioPanel] Auto-saved script');

        // Dispatch event for cross-panel sync (VideoScriptsPanel listens for this)
        window.dispatchEvent(new CustomEvent('script-updated', {
          detail: {
            conversationId,
            slideId,
            scriptText: script,
            timestamp: new Date().toISOString(),
          },
        }));
      } catch (err) {
        console.error('[ScriptAudioPanel] Auto-save script failed:', err);
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [script, hasUnsavedChanges, slideId, audioVersion, selectedVoice, voices, conversationId]);

  // Auto-save prompt changes (debounced 1s)
  useEffect(() => {
    if (!hasUnsavedPrompts) return;

    const timeoutId = setTimeout(async () => {
      try {
        setIsSaving(true);
        await updateSlidePrompts(slideId, {
          promptFlash,
          promptPro,
          preferredModel: selectedModel,
        });
        setHasUnsavedPrompts(false);
        console.log('[ScriptAudioPanel] Auto-saved prompts');
      } catch (err) {
        console.error('[ScriptAudioPanel] Auto-save prompts failed:', err);
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [promptFlash, promptPro, selectedModel, hasUnsavedPrompts, slideId]);

  // Track if this is the initial voice load to avoid overwriting user selection
  const initialVoiceLoadedRef = useRef(false);
  const shownSlideIdRef = useRef(slideId);
  shownSlideIdRef.current = slideId;
  // Only a voice picked in this panel is saved: the video's voice setting shown on open is not a change to the slide.
  const voicePickedRef = useRef(false);
  const pickVoice = useCallback((voiceId: string) => { voicePickedRef.current = true; setSelectedVoice(voiceId); }, []);
  useEffect(() => { voicePickedRef.current = false; }, [slideId]);

  // Auto-save voice changes (immediate when audioVersion exists)
  useEffect(() => {
    // Skip if no audio version exists or if voice matches what's already saved
    if (!audioVersion || !initialVoiceLoadedRef.current || !voicePickedRef.current) return;
    if (selectedVoice === audioVersion.voiceId) return;

    const saveVoice = async () => {
      try {
        setIsSaving(true);
        const selectedVoiceObj = voices.find((v) => v.id === selectedVoice);
        await updateScript(slideId, audioVersion.scriptText, {
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
        });
        // Update local state to match
        setAudioVersion(prev => prev ? {
          ...prev,
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
        } : prev);
        voicePickedRef.current = false;
        console.log('[ScriptAudioPanel] Auto-saved voice:', selectedVoice);
      } catch (err) {
        console.error('[ScriptAudioPanel] Auto-save voice failed:', err);
      } finally {
        setIsSaving(false);
      }
    };

    // Small delay to debounce rapid changes
    const timeoutId = setTimeout(saveVoice, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedVoice, audioVersion?.voiceId, slideId, voices]);

  // Listen for real-time audio events via SSE (replaces polling)
  useEffect(() => {
    const handleAudioEvent = (event: CustomEvent) => {
      const { audioEvent } = event.detail;
      // Extract traceId for end-to-end tracing
      const traceId = audioEvent.traceId || 'unknown';

      // Log every event received, before filtering
      console.log(`🎧 [AUDIO-TRACE:${traceId}] ScriptAudioPanel received audio event`, {
        eventSlideId: audioEvent.slideId,
        componentSlideId: slideId,
        matches: audioEvent.slideId === slideId,
        type: audioEvent.type,
        progress: audioEvent.progress,
        status: audioEvent.status,
      });

      // Only handle events for this slide
      if (audioEvent.slideId !== slideId) {
        console.log(`🎧 [AUDIO-TRACE:${traceId}] Filtering out - slideId mismatch (event: ${audioEvent.slideId}, component: ${slideId})`);
        return;
      }

      console.log(`🎧 [AUDIO-TRACE:${traceId}] Processing audio event for matching slide`, audioEvent);

      // Update status based on event type
      if (audioEvent.type === 'audio_status') {
        console.log(`🎧 [AUDIO-TRACE:${traceId}] Updating status: progress=${audioEvent.progress}, message=${audioEvent.message}`);
        setAudioVersion((prev) => prev ? {
          ...prev,
          status: audioEvent.status || prev.status,
        } : prev);
        // Update progress info
        if (audioEvent.progress !== undefined) {
          setGenerationProgress(audioEvent.progress);
        }
        if (audioEvent.message) {
          setGenerationMessage(audioEvent.message);
        }
        // IMPORTANT: Set isGenerating to true when we receive GENERATING or ALIGNING status
        // This ensures progress UI stays visible even when triggered by parent's "Generate All"
        if (audioEvent.status === 'GENERATING' || audioEvent.status === 'ALIGNING') {
          isGeneratingRef.current = true;  // Update ref immediately (state updates are async)
          setIsGenerating(true);
          if (!generationStartTime) {
            setGenerationStartTime(Date.now());
          }
        }
        // Handle terminal statuses received via audio_status event (fallback path)
        if (audioEvent.status === 'COMPLETED' || audioEvent.status === 'FAILED') {
          console.log(`🎧 [AUDIO-TRACE:${traceId}] Terminal status via audio_status: ${audioEvent.status}`);
          // Only reset generation state if this is genuinely a new completion
          // (i.e., we have a new version number or the event includes the new version)
          const eventVersion = audioEvent.version;
          const startVersion = generationStartVersionRef.current;
          const isNewVersion = eventVersion && startVersion !== null && eventVersion > startVersion;

          if (audioEvent.status === 'COMPLETED') {
            setGenerationProgress(100);
            // Only clear generating state if we have a new version (generation truly complete)
            // or if we weren't tracking a version (fallback for older events)
            if (isNewVersion || startVersion === null) {
              console.log(`🎧 [AUDIO-TRACE:${traceId}] Generation complete - new version detected (${startVersion} -> ${eventVersion})`);
              isGeneratingRef.current = false;
              generationStartVersionRef.current = null;
              setIsGenerating(false);
              setGenerationStartTime(null);
            }
            loadAudioData();
          } else {
            // FAILED status - always reset
            isGeneratingRef.current = false;
            generationStartVersionRef.current = null;
            setIsGenerating(false);
            setGenerationStartTime(null);
          }
        }
      } else if (audioEvent.type === 'audio_generated') {
        // Audio generation complete - reload full data to get new audio URL and timings
        console.log(`🎧 [AUDIO-TRACE:${traceId}] Audio generation complete, reloading data`);
        setGenerationProgress(100);
        setGenerationMessage('Complete!');
        isGeneratingRef.current = false;  // Update ref immediately before loadAudioData
        generationStartVersionRef.current = null;  // Clear version tracking
        setIsGenerating(false); // Reset generating state on completion
        setGenerationStartTime(null);
        loadAudioData();
      } else if (audioEvent.type === 'audio_error') {
        console.log(`🎧 [AUDIO-TRACE:${traceId}] Audio generation error: ${audioEvent.error}`);
        setError(audioEvent.error || 'Audio generation failed');
        setAudioVersion((prev) => prev ? { ...prev, status: 'FAILED' } : prev);
        isGeneratingRef.current = false;  // Update ref immediately
        generationStartVersionRef.current = null;  // Clear version tracking
        setIsGenerating(false); // Reset generating state on error
        setGenerationStartTime(null);
        setGenerationProgress(0);
        setGenerationMessage('');
      }
    };

    console.log(`🎧 [ScriptAudioPanel] Setting up audio event listener for slideId: ${slideId}`);
    window.addEventListener('audio-event', handleAudioEvent as EventListener);
    return () => {
      console.log(`🎧 [ScriptAudioPanel] Removing audio event listener for slideId: ${slideId}`);
      window.removeEventListener('audio-event', handleAudioEvent as EventListener);
    };
  }, [slideId]);

  // Listen for audio settings changes from VideoScriptsPanel (All Scripts panel)
  useEffect(() => {
    const handleSettingsChange = (event: CustomEvent<{ voiceId?: string; voiceName?: string; ttsModel?: string; conversationId: string }>) => {
      // Only update if this is for our conversation (use parent ID for video-level settings)
      const targetConversationId = parentConversationId || conversationId;
      if (event.detail.conversationId !== targetConversationId) return;

      // Only skip if audio is actively being generated (protect in-progress work)
      if (audioVersion?.status === 'GENERATING' || audioVersion?.status === 'ALIGNING') {
        console.log('🎧 [ScriptAudioPanel] Ignoring settings change - audio generation in progress');
        return;
      }

      if (event.detail.voiceId) {
        console.log('🎧 [ScriptAudioPanel] Updating voice from settings change:', event.detail.voiceId);
        pickVoice(event.detail.voiceId);
      }

      if (event.detail.ttsModel) {
        console.log('🎧 [ScriptAudioPanel] Updating model from settings change:', event.detail.ttsModel);
        setSelectedModel(event.detail.ttsModel as 'flash' | 'pro');
      }
    };

    window.addEventListener('audio-settings-changed', handleSettingsChange as EventListener);
    return () => {
      window.removeEventListener('audio-settings-changed', handleSettingsChange as EventListener);
    };
  }, [conversationId, parentConversationId, audioVersion?.status]);

  // Fallback polling for status updates when generating (in case SSE fails)
  useEffect(() => {
    // Poll when user initiated generation OR when server status shows generating/aligning
    const shouldPoll = isGeneratingRef.current ||
                       audioVersion?.status === 'GENERATING' ||
                       audioVersion?.status === 'ALIGNING';

    if (shouldPoll) {
      // Use 2s interval to match batch generation behavior - more responsive feedback
      const interval = setInterval(() => {
        loadAudioData();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [audioVersion?.status, isGenerating]);

  // Track generation progress
  useEffect(() => {
    if (audioVersion?.status === 'GENERATING' && !generationStartTime) {
      setGenerationStartTime(Date.now());
    } else if (audioVersion?.status === 'COMPLETED' || audioVersion?.status === 'FAILED') {
      setGenerationStartTime(null);
    }
  }, [audioVersion?.status, generationStartTime]);

  // Load audio data for the slide
  const loadAudioData = async () => {
    if (!slideId) {
      console.log('🎧 [ScriptAudioPanel] Skipping loadAudioData - no slideId');
      return;
    }
    console.log('🎧 [ScriptAudioPanel] Loading audio data for slideId:', slideId);

    // Only show loading state if we're NOT generating
    // This prevents the progress UI from being hidden during generation
    const shouldShowLoading = !isGeneratingRef.current;
    if (shouldShowLoading) {
      setIsLoading(true);
    }

    try {
      const audio = await getSlideAudio(slideId);
      console.log('🎧 [ScriptAudioPanel] Audio data loaded:', { audio, slideId });
      setAudioVersion(audio);

      // Capture applied version info for tracking which audio is applied to slide
      if (audio?.appliedVersion) {
        setAppliedVersion(audio.appliedVersion);
      }

      if (audio) {
        setScript(audio.scriptText);
        // The picker's voice is resolved by loadVoices() from this audio's voiceId (see there)
        setHasUnsavedChanges(false);

        const allVersions = await listAudioVersions(slideId);
        console.log('🎧 [ScriptAudioPanel] Audio versions loaded:', { count: allVersions.length, versions: allVersions.map(v => ({ version: v.version, status: v.status })) });
        setVersions(allVersions);

        // Safety net: Reset isGenerating when status is COMPLETED or FAILED
        // BUT only if we're not in a user-initiated generation (isGeneratingRef)
        // This prevents flicker when polling returns old/stale COMPLETED status
        // while we're actively generating a new version
        if ((audio.status === 'COMPLETED' || audio.status === 'FAILED') && !isGeneratingRef.current) {
          setIsGenerating(false);
          setGenerationStartTime(null);
        }

        // If user initiated generation and we got COMPLETED with a NEW version, reset
        if (audio.status === 'COMPLETED' && isGeneratingRef.current && generationStartVersionRef.current !== null) {
          const newVersion = audio.version;
          if (newVersion > generationStartVersionRef.current) {
            console.log(`🎧 [ScriptAudioPanel] New version detected via polling: ${generationStartVersionRef.current} -> ${newVersion}`);
            isGeneratingRef.current = false;
            generationStartVersionRef.current = null;
            setIsGenerating(false);
            setGenerationStartTime(null);
          }
        }

        if (audio.status === 'COMPLETED' && audio.audioUrl && audio.wordTimings) {
          onAudioGenerated?.(audio.audioUrl, audio.wordTimings);
        }
      }
      return audio;
    } catch (err: any) {
      console.error('Failed to load audio data:', err);
      setError(err.message);
      return null;
    } finally {
      // Only clear loading if we set it (not during generation)
      if (shouldShowLoading) {
        setIsLoading(false);
      }
    }
  };

  const narration = useNarrationAudioSrc(
    audioVersion?.status === 'COMPLETED' ? slideId : null,
    audioVersion?.version,
    audioVersion?.audioUrl,
  );

  // The picker drives Regenerate for this slide, so it starts on the slide's own voice (the one its narration was
  // made or saved with). The video's voice setting is for slides with no narration yet: the settings GET mints a Kore
  // row on any video where nobody chose one, so it cannot outrank a slide's real voice. Model still comes from the
  // video's setting. Re-resolved on every slide switch; a slower load for a slide no longer shown is dropped.
  const loadVoices = async (forSlideId: string, slideAudio: Promise<SlideAudioResponse | null | undefined>) => {
    try {
      const catalog = await getAvailableVoices();
      setVoices(catalog.voices);
      const settingsConversationId = parentConversationId || conversationId;
      const [own, conversationSettings] = await Promise.all([
        slideAudio,
        getAudioSettings(settingsConversationId).catch(() => null),
      ]);
      if (shownSlideIdRef.current !== forSlideId) return;
      if (conversationSettings?.ttsModel) setSelectedModel(conversationSettings.ttsModel.includes('pro') ? 'pro' : 'flash');
      const voice = own?.voiceId || conversationSettings?.voiceId || catalog.defaultVoice;
      console.log('🎧 [ScriptAudioPanel] Voice for slide', forSlideId, ':', voice, own?.voiceId ? '(slide)' : conversationSettings?.voiceId ? '(video setting)' : '(catalog default)');
      setSelectedVoice(voice);
      initialVoiceLoadedRef.current = true;
    } catch (err: any) {
      console.error('Failed to load voices:', err);
    }
  };

  // Load TTS prompts
  const loadPrompts = async () => {
    if (!slideId) {
      console.log('🎧 [ScriptAudioPanel] Skipping loadPrompts - no slideId');
      return;
    }
    try {
      const prompts = await getSlidePrompts(slideId);
      if (prompts) {
        setPromptFlash(prompts.promptFlash || '');
        setPromptPro(prompts.promptPro || '');
        setHasUnsavedPrompts(false);
        setIsVideoLevelPrompts(prompts.isVideoLevel || false);
        if (prompts.isVideoLevel) {
          console.log('🎧 [ScriptAudioPanel] Using video-level TTS prompts');
        }
      }
    } catch (err: any) {
      console.error('Failed to load prompts:', err);
    }
  };

  // Handle prompt changes
  const handlePromptChange = (value: string) => {
    if (selectedModel === 'flash') {
      setPromptFlash(value);
    } else {
      setPromptPro(value);
    }
    setHasUnsavedPrompts(true);
  };

  // Handle script changes
  const handleScriptChange = (value: string) => {
    setScript(value);
    setHasUnsavedChanges(value !== (audioVersion?.scriptText || initialScript));
    onScriptChange?.(value);
  };

  // Save script to SlideAudio (does NOT create a new version)
  // Versions are only created when audio is actually generated
  const handleSaveScript = async () => {
    if (!script.trim()) {
      setError('Script cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const selectedVoiceObj = voices.find((v) => v.id === selectedVoice);

      // Update script on SlideAudio - no version created
      await updateScript(slideId, script, {
        voiceId: selectedVoice,
        voiceName: selectedVoiceObj?.name || selectedVoice,
      });

      // Update local audioVersion with new script/voice if it exists
      if (audioVersion) {
        setAudioVersion(prev => prev ? {
          ...prev,
          scriptText: script,
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
        } : prev);
      }

      // Dispatch event for cross-panel sync (VideoScriptsPanel listens for this)
      window.dispatchEvent(new CustomEvent('script-updated', {
        detail: {
          conversationId,
          slideId,
          scriptText: script,
          timestamp: new Date().toISOString(),
        },
      }));

      setHasUnsavedChanges(false);
    } catch (err: any) {
      console.error('Failed to save script:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Generate audio
  const handleGenerateAudio = async () => {
    try {
      isGeneratingRef.current = true;  // Update ref immediately
      // Track the current version so we can detect when a NEW version is created
      generationStartVersionRef.current = audioVersion?.version ?? 0;
      console.log(`🎧 [ScriptAudioPanel] Starting generation, current version: ${generationStartVersionRef.current}`);

      setIsGenerating(true);
      setError(null);
      setGenerationProgress(0);
      setGenerationMessage('Queuing generation...');

      if (hasUnsavedChanges) {
        await handleSaveScript();
      }

      const selectedVoiceObj = voices.find((v) => v.id === selectedVoice);

      if (!audioVersion) {
        const created = await createAudioVersion(slideId, script, {
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
          generateImmediately: true,
        });
        // Optimistically set status to GENERATING so polling starts
        setAudioVersion({ ...created, status: 'GENERATING' });
        setGenerationStartTime(Date.now());
      } else {
        // Pass current UI selections when regenerating
        await generateAudio(slideId, {
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
          ttsModel: selectedModel,
        });
        // Optimistically set status to GENERATING so polling starts
        setAudioVersion((prev) => prev ? {
          ...prev,
          status: 'GENERATING',
          voiceId: selectedVoice,
          voiceName: selectedVoiceObj?.name || selectedVoice,
        } : prev);
        setGenerationStartTime(Date.now());
      }

      // Don't call loadAudioData here - let polling handle it
      // The status is already set to GENERATING which triggers the polling useEffect
      // Note: We don't set isGenerating to false here - it will be set false by SSE events
      // (audio_generated or audio_error) to prevent the progress UI from flickering
    } catch (err: any) {
      console.error('Failed to generate audio:', err);
      setError(err.response?.data?.error || err.message);
      // Reset generation state on error (during queuing)
      setGenerationStartTime(null);
      isGeneratingRef.current = false;  // Update ref immediately
      generationStartVersionRef.current = null;  // Clear version tracking
      setIsGenerating(false); // Only reset on queuing error
    }
    // Note: No finally block - isGenerating is reset by SSE events when generation completes
  };

  // Switch to a different version
  const handleSwitchVersion = async (version: number) => {
    try {
      setIsLoading(true);
      setError(null);

      // Stop any currently playing audio and reset state
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      setCurrentTime(0);
      setCurrentWordIndex(-1);
      setIsAudioReady(false);
      setAudioLoadError(null);

      // Call API to switch version
      await switchAudioVersion(slideId, version);

      // Reload audio data to get the switched-to take with a fresh signed URL
      await loadAudioData();

      setHasUnsavedChanges(false);
    } catch (err: any) {
      console.error('Failed to switch version:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Track audio load state
  const [audioLoadError, setAudioLoadError] = useState<string | null>(null);
  const [_isAudioReady, setIsAudioReady] = useState(false);

  // Audio playback handlers
  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        setAudioLoadError(null);
      } catch (err: any) {
        console.error('Failed to play audio:', err);
        setAudioLoadError('Failed to play audio. Try regenerating.');
        setIsPlaying(false);
      }
    }
  };

  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleRestart = async () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        setAudioLoadError(null);
      } catch (err: any) {
        console.error('Failed to restart audio:', err);
        setAudioLoadError('Failed to play audio. Try regenerating.');
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current || !audioVersion?.wordTimings) return;

    const time = audioRef.current.currentTime;
    setCurrentTime(time);

    const timings = audioVersion.wordTimings as WordTiming[];
    const wordIdx = timings.findIndex(
      (wt) => time >= wt.startTime && time <= wt.endTime
    );
    setCurrentWordIndex(wordIdx);
  }, [audioVersion?.wordTimings]);

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentWordIndex(-1);
  };

  const handleWordClick = async (time: number) => {
    handleSeek(time);
    if (!isPlaying && audioRef.current) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        setAudioLoadError(null);
      } catch (err: any) {
        console.error('Failed to play audio from word click:', err);
        setAudioLoadError('Failed to play audio. Try regenerating.');
      }
    }
  };

  // Note: SSE subscriptions and reconnection are now managed by useApplyJobStatus hook

  // Listen for slides-updated events to clear choreography job ID when choreography completes
  useEffect(() => {
    const handleSlidesUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: eventConvId, source } = customEvent.detail || {};

      // Only clear if it's from animation choreographer and matches our conversation
      if (eventConvId === conversationId && source === 'animation_choreographer') {
        console.log('[ScriptAudioPanel] Choreography completed via slides-updated event');
        setChoreographyJobId(null);
        // Optionally refresh applied version
        loadAudioData();
      }
    };

    window.addEventListener('slides-updated', handleSlidesUpdated);
    return () => window.removeEventListener('slides-updated', handleSlidesUpdated);
  }, [conversationId]);

  // Handle cancel sync
  const handleCancelSync = async () => {
    if (!choreographyJobId) return;
    try {
      await cancelAgentJob(choreographyJobId);
      // Note: The hook will handle state cleanup via SSE completion/error events
    } catch (err) {
      console.error('[ScriptAudioPanel] Failed to cancel sync:', err);
    }
    setChoreographyJobId(null);
  };

  // Handle applying audio to slide with optional animation choreography
  // Uses shared function + hook for state management (same as NarrationAudioControls)
  const handleApply = async () => {
    if (!audioVersion || audioVersion.status !== 'COMPLETED' || isSyncingAnimations) {
      setApplyError('No completed audio to apply');
      return;
    }

    setSyncError(null);
    setApplyError(null);

    try {
      setIsApplyingAudio(true);

      const result = await applyAudioWithChoreography({
        slideId,
        slideIdentifier: slideIdentifier || slideId,
        conversationId,
        versionId: audioVersion.id,
        scriptText: script,
        registerJob: (slideId, jobId) => {
          // Save job ID for cancellation tracking
          setChoreographyJobId(jobId);
          // Register with hook for SSE tracking and progress updates
          registerApplyJob(slideId, jobId);
        },
      });

      if (!result.success) {
        setApplyError(result.error || 'Failed to apply audio');
        return;
      }

      // Update applied version tracking
      setAppliedVersion({
        id: audioVersion.id,
        version: audioVersion.version,
        voiceName: audioVersion.voiceName,
        voiceId: audioVersion.voiceId,
        audioDuration: audioVersion.audioDuration,
        appliedAt: new Date().toISOString(),
      });

      if (result.choreographyJobCreated) {
        // Choreography started - hook will track progress
        console.log('[ScriptAudioPanel] Choreography job created, syncing animations');
      } else {
        // No choreography needed - audio applied successfully
        console.log('[ScriptAudioPanel] Audio applied (no word timings)');
        // Dispatch event for slide preview refresh
        window.dispatchEvent(new CustomEvent('slides-updated', {
          detail: {
            conversationId,
            slideId,
            timestamp: new Date().toISOString(),
            source: 'audio_apply',
          }
        }));
      }
    } catch (err: any) {
      console.error('[ScriptAudioPanel] Failed to apply audio:', err);
      setApplyError(err.response?.data?.error || err.message || 'Failed to apply audio');
    } finally {
      setIsApplyingAudio(false);
    }
  };

  // Get status display
  const isGeneratingOrAligning = audioVersion?.status === 'GENERATING' || audioVersion?.status === 'ALIGNING';
  const hasAudio = audioVersion?.status === 'COMPLETED' && !!narration.src;
  // Note: hasWordTimings check is now done inside applyAudioWithChoreography shared function

  // Show loading state
  if (isLoading) {
    return (
      <div className={cn('flex flex-col h-full items-center justify-center bg-background', className)}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading audio data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Card 1: Audio Review (only when audio exists and not generating) */}
          {hasAudio && !isGeneratingOrAligning && (
            <AudioReviewCard
              versions={versions}
              currentVersion={audioVersion}
              appliedVersion={appliedVersion}
              onVersionChange={handleSwitchVersion}
              audioSrc={narration.src}
              audioRef={audioRef as React.RefObject<HTMLAudioElement>}
              isPlaying={isPlaying}
              currentTime={currentTime}
              duration={duration}
              onPlayPause={handlePlayPause}
              onSeek={handleSeek}
              onRestart={handleRestart}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleAudioEnded}
              onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
              onCanPlayThrough={() => {
                setIsAudioReady(true);
                setAudioLoadError(null);
              }}
              onAudioError={() => {
                setIsAudioReady(false);
                setIsPlaying(false);
                narration.onError();
              }}
              wordTimings={(audioVersion?.wordTimings as WordTiming[]) || null}
              currentWordIndex={currentWordIndex}
              onWordClick={handleWordClick}
              script={script}
              onApply={SHOW_NARRATION_ADVANCED ? handleApply : undefined}
              isApplying={SHOW_NARRATION_ADVANCED ? (isApplyingAudio || isSyncingAnimations) : false}
              applyProgress={SHOW_NARRATION_ADVANCED && syncProgress ? {
                percentage: syncProgress.percentage ?? 0,
                currentOperation: syncProgress.currentOperation ?? 'Processing...',
                thinking: syncProgress.thinking,
              } : null}
              onCancelApply={SHOW_NARRATION_ADVANCED && choreographyJobId ? handleCancelSync : undefined}
              applyError={SHOW_NARRATION_ADVANCED ? (applyError || syncError) : null}
              audioLoadError={audioLoadError ?? (narration.error && 'Failed to load audio. Try regenerating.')}
              isLoadingAudio={narration.loading}
            />
          )}

          {/* Generating Overlay */}
          {(isGenerating || isGeneratingOrAligning) && (
            <GeneratingOverlay
              progress={generationProgress}
              status={audioVersion?.status}
              thinking={syncProgress?.thinking}
              script={script}
            />
          )}

          {/* Card 2: Script & Voice (always visible) */}
          <ScriptVoiceCard
            defaultExpanded={!hasAudio}
            script={script}
            wordCount={script.split(/\s+/).filter(Boolean).length}
            onScriptChange={handleScriptChange}
            hasUnsavedChanges={hasUnsavedChanges}
            voices={voices}
            selectedVoice={selectedVoice}
            onVoiceChange={pickVoice}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            promptValue={selectedModel === 'flash' ? promptFlash : promptPro}
            onPromptChange={handlePromptChange}
            isVideoLevelPrompts={isVideoLevelPrompts}
            hasAudio={!!hasAudio}
            onGenerate={SHOW_NARRATION_ADVANCED ? handleGenerateAudio : undefined}
            isGenerating={isGenerating || isGeneratingOrAligning}
            isLoading={isLoading}
          />

          {/* Interaction gate timings (interactive-edu slides). Renders nothing
              when the slide has no gates. Saves through the unified slide-source
              pipeline; the player reloads via the slides-updated SSE. */}
          <GateTimingEditor
            conversationId={conversationId}
            slideId={slideIdentifier || slideId}
            dbSlideId={slideId}
            appliedAudioVersion={appliedVersion?.version}
          />

          {/* Global Error Display */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-start gap-2">
                <div className="h-4 w-4 text-destructive mt-0.5">⚠</div>
                <div>
                  <p className="text-sm font-medium text-destructive">Error</p>
                  <p className="text-xs text-destructive/80">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ScriptAudioPanel;
